import React, { useEffect, useState } from "react";
import { BookOpen, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Strategy reference docs — editable singletons that ground the AI on a brand
 * playbook. The first one shipped is "Substack Strategy — Foundation". Adding
 * more is just inserting another seed in the backend's STRATEGY_DOC_SEEDS.
 */
export default function Strategy() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({ title: "", body_markdown: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.listStrategyDocs();
        setDocs(list);
        if (list.length) {
          setActive(list[0]);
          setDraft({ title: list[0].title, body_markdown: list[0].body_markdown });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openDoc = (d) => {
    setActive(d);
    setDraft({ title: d.title, body_markdown: d.body_markdown });
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const updated = await api.updateStrategyDoc(active.slug, {
        title: draft.title,
        body_markdown: draft.body_markdown,
      });
      setActive(updated);
      setDocs((prev) => prev.map((d) => (d.slug === updated.slug ? updated : d)));
      toast.success("Strategy saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    if (!active) return;
    setDraft({ title: active.title, body_markdown: active.body_markdown });
  };

  const dirty = active && (draft.title !== active.title || draft.body_markdown !== active.body_markdown);

  if (loading) return <div className="text-eyebrow">Loading…</div>;

  return (
    <div className="max-w-5xl" data-testid="strategy-page">
      <div className="mb-8">
        <div className="text-eyebrow mb-3 flex items-center gap-2"><BookOpen size={14} /> Knowledge layer</div>
        <h1 className="font-display font-black text-5xl tracking-tighter">Strategy</h1>
        <p className="text-[#52525B] mt-3 max-w-2xl">
          Editable reference docs that ground the writer on platform-specific growth strategy.
          Edit them like you'd edit a brand voice doc — every change updates the source of truth.
        </p>
      </div>

      {docs.length === 0 ? (
        <div className="border-2 border-dashed border-[#E2E4E9] p-16 text-center" data-testid="strategy-empty">
          <p className="font-display font-bold text-2xl">No strategy docs yet</p>
          <p className="text-sm text-[#52525B] mt-2">Restart the backend to seed the foundation docs.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-[240px_1fr] gap-6">
          <aside className="border-r border-[#E2E4E9]" data-testid="strategy-list">
            <ul className="space-y-1">
              {docs.map((d) => (
                <li key={d.slug}>
                  <button
                    onClick={() => openDoc(d)}
                    data-testid={`strategy-tab-${d.slug}`}
                    className={`w-full text-left px-3 py-3 text-sm border-l-4 transition-colors ${
                      active?.slug === d.slug
                        ? "border-[#0A0A0B] bg-[#F4F5F6] font-semibold"
                        : "border-transparent hover:bg-[#F4F5F6]"
                    }`}
                  >
                    {d.title}
                    <div className="text-[10px] mono uppercase text-[#A1A1AA] mt-1">{d.kind || "doc"}</div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {active && (
            <div data-testid="strategy-editor">
              <label className="text-eyebrow block mb-2">Title</label>
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                data-testid="strategy-title-input"
                className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none mb-5"
              />
              <label className="text-eyebrow block mb-2">Body (Markdown)</label>
              <textarea
                value={draft.body_markdown}
                onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })}
                rows={22}
                data-testid="strategy-body-input"
                className="w-full border border-[#E2E4E9] focus:border-black p-4 outline-none font-mono text-sm leading-relaxed"
              />
              <p className="text-xs mono uppercase text-[#A1A1AA] mt-2">
                slug: {active.slug} · last updated {active.updated_at?.slice(0, 16).replace("T", " ")}
              </p>
              <div className="mt-5 flex gap-3 justify-end">
                <button
                  onClick={revert}
                  disabled={!dirty}
                  data-testid="strategy-revert"
                  className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6] disabled:opacity-40 disabled:hover:bg-transparent flex items-center gap-2"
                >
                  <RotateCcw size={14} /> Revert
                </button>
                <button
                  onClick={save}
                  disabled={!dirty || saving}
                  data-testid="strategy-save"
                  className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7] disabled:opacity-40 disabled:hover:bg-[#0A0A0B] flex items-center gap-2"
                >
                  <Save size={14} /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
